/**
 * Shared field-level validators for Hub UI form primitives.
 *
 * Pure functions, no React, no async, no I/O. Each returns `null` for
 * valid input or a single human-readable error string. Form primitives
 * call these from a `validate?: (value) => string | null` prop so pages
 * don't redeclare the same regex/length checks across files.
 *
 * Page-level aggregate validators such as `validateAgentForm` still own
 * multi-field cross-checks and the submit-time gate. The primitives consume
 * the field-level validators here for inline blur/change feedback.
 */

import { validateAccentColor } from "@lifter/hub-ui/browser-shared";

// Lifted from RFC 5322's practical subset — enough to catch typos
// without rejecting legitimate addresses. The server enforces the
// canonical rule; this just keeps obvious typos out of the round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

// Mirrors `HANDLE_PATTERN` in the browser-shared `agent-forms.ts`. The
// aggregate `validateAgentForm` and this field-level validator must
// agree on the error string so the message a user sees on blur matches
// what the submit-time guard would show.
const AGENT_HANDLE_PATTERN = /^[a-z][a-z0-9-]*$/;
const RESERVED_AGENT_HANDLES = new Set(["everyone", "here", "channel", "admin"]);
const AGENT_HANDLE_MIN = 2;
const AGENT_HANDLE_MAX = 32;
const AGENT_HANDLE_ERROR =
  "Handle must be 2-32 lowercase letters, digits, or hyphens, starting with a letter, and not a reserved word.";

export function validateRequired(value: string, label = "This field"): string | null {
  if (value.trim().length === 0) return `${label} is required`;
  return null;
}

export function validateEmail(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Enter an email address.";
  if (!EMAIL_PATTERN.test(trimmed)) return "Enter a valid email address.";
  return null;
}

export function validateAgentHandle(value: string): string | null {
  const handle = value.trim();
  if (handle.length === 0) return null;
  if (handle.length < AGENT_HANDLE_MIN || handle.length > AGENT_HANDLE_MAX) {
    return AGENT_HANDLE_ERROR;
  }
  if (!AGENT_HANDLE_PATTERN.test(handle)) return AGENT_HANDLE_ERROR;
  if (RESERVED_AGENT_HANDLES.has(handle)) return AGENT_HANDLE_ERROR;
  return null;
}

/**
 * Accent-color field rule, delegating to the shared validator the server
 * runs on `PATCH /api/org/branding` (hex format; contrast is advisory-only
 * and rendered as a separate warning, never an error). The delegation is
 * load-bearing: the form primitives treat a passing live validator as
 * authoritative over submit-time errors, so this validator must always
 * cover the full server rule or rejections become silent no-saves.
 */
export function validateAccentColorField(value: string): string | null {
  const result = validateAccentColor(value.trim());
  return result.valid ? null : (result.error ?? null);
}

export interface ValidateLengthOptions {
  min?: number;
  max?: number;
  label?: string;
}

export function validateLength(value: string, opts: ValidateLengthOptions): string | null {
  const trimmed = value.trim();
  const label = opts.label ?? "This field";
  if (opts.min !== undefined && trimmed.length < opts.min) {
    return `${label} must be at least ${opts.min} characters`;
  }
  if (opts.max !== undefined && trimmed.length > opts.max) {
    return `${label} must be ${opts.max} characters or fewer`;
  }
  return null;
}
